import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { isLeagueCreationAllowedOnStandalonePage } from '../config/leagueCreation';

interface LiveTournament {
  id: number;
  name: string;
  format: string;
  year: number;
  status: string;
}

const CreateLeaguePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<LiveTournament[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [selectedLeagueType, setSelectedLeagueType] = useState(() => {
    const fromUrl = searchParams.get('type') || '';
    if (fromUrl && isLeagueCreationAllowedOnStandalonePage(fromUrl)) return fromUrl;
    return 'pickem';
  });
  const [maxParticipants, setMaxParticipants] = useState(10);

  useEffect(() => {
    checkAuthStatus();
    fetchTournaments();
  }, []);

  const checkAuthStatus = () => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  };

  const fetchTournaments = async () => {
    try {
      const data = await apiService.getTournaments();

      if (data.success) {
        setTournaments(data.tournaments || []);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    }
  };

  const getLeagueTypeInfo = (type: string) => {
    switch (type) {
      case 'pickem':
        return {
          name: 'Pick\'em League',
          icon: '🎯',
          description: 'Predict match outcomes and earn points based on accuracy',
          color: 'from-blue-600 to-purple-600'
        };
      case 'budget_league':
        return {
          name: 'Budget League',
          icon: '💰',
          description: 'Build teams within budget constraints and compete for prizes',
          color: 'from-green-600 to-blue-600'
        };
      case 'draft_league':
        return {
          name: 'Draft League',
          icon: '📋',
          description: 'Draft players in a snake draft format with friends',
          color: 'from-orange-600 to-red-600'
        };
      case 'h2h':
        return {
          name: 'Head-to-Head League',
          icon: '⚔️',
          description: 'Face off against opponents in one-on-one battles',
          color: 'from-red-600 to-purple-600'
        };
      case 'eliminator':
        return {
          name: 'Eliminator League',
          icon: '💀',
          description: 'Survival tournament - get eliminated after wrong picks',
          color: 'from-red-600 to-black'
        };
      default:
        return {
          name: 'Fantasy League',
          icon: '🏏',
          description: 'Create a new fantasy cricket league',
          color: 'from-blue-600 to-purple-600'
        };
    }
  };

  const leagueInfo = getLeagueTypeInfo(selectedLeagueType);

  // Auto-adjust participant count based on league type
  useEffect(() => {
    if (selectedLeagueType === 'h2h') {
      setMaxParticipants(2);
    } else if (selectedLeagueType === 'draft_league') {
      setMaxParticipants(8); // Default even number for draft
    } else {
      setMaxParticipants(10); // Default for other types
    }
  }, [selectedLeagueType]);

  const handleCreateLeague = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!isLoggedIn) {
      navigate('/tournaments');
      return;
    }

    if (!isLeagueCreationAllowedOnStandalonePage(selectedLeagueType)) {
      setError('This league type cannot be created on this page. Use Live Tournaments for H2H (with full setup).');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(event.currentTarget);
    const leagueData = {
      name: formData.get('name') as string,
      live_tournament_id: parseInt(formData.get('tournament') as string),
      league_type: selectedLeagueType,
      max_participants: maxParticipants,
      is_public: formData.get('is_public') === 'true',
      entry_fee: 0,
      prize_pool: 0
    };

    try {
      const data = await apiService.createFantasyLeague(leagueData);

      if (data.success) {
        setSuccess('League created successfully!');
        setTimeout(() => {
          // Navigate to My Leagues section instead of directly to the league
          navigate('/my-leagues');
        }, 2000);
      } else {
        setError(data.message || 'Failed to create league');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create league');
      console.error('Error creating league:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">{leagueInfo.icon}</div>
          <h1 className="text-3xl font-bold text-white mb-4">Create {leagueInfo.name}</h1>
          <p className="text-white/60 mb-8">Please log in to create a league</p>
          <button
            onClick={() => navigate('/tournaments')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">{leagueInfo.icon}</div>
        <h1 className="text-3xl font-bold text-white mb-4">Create {leagueInfo.name}</h1>
        <p className="text-white/60">{leagueInfo.description}</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6">
          <p className="text-green-400 text-center">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <p className="text-red-400 text-center">{error}</p>
        </div>
      )}

      {/* Create League Form */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20">
        <form onSubmit={handleCreateLeague} className="space-y-6">
          {/* League Name */}
          <div>
            <label htmlFor="name" className="block text-white font-medium mb-2">
              League Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter league name"
            />
          </div>

          {/* League Type Selection */}
          <div>
            <label htmlFor="league_type" className="block text-white font-medium mb-2">
              League Type *
            </label>
            <p className="text-xs text-white/50 mb-2">
              Pick&apos;em and Budget leagues can be created here. For H2H, use Home → Live → pick a tournament → Create League (modal includes match/series options).
            </p>
            <select
              id="league_type"
              name="league_type"
              value={selectedLeagueType}
              onChange={(e) => {
                if (!isLeagueCreationAllowedOnStandalonePage(e.target.value)) return;
                setSelectedLeagueType(e.target.value);
              }}
              required
              className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pickem" className="text-gray-800">Pick&apos;em League</option>
              <option value="budget_league" disabled={!isLeagueCreationAllowedOnStandalonePage('budget_league')} className="text-gray-800">
                Budget League
              </option>
              <option value="draft_league" disabled className="text-gray-800">
                Draft League (unavailable)
              </option>
              <option value="h2h" disabled className="text-gray-800">
                H2H League (use Live Tournaments)
              </option>
              <option value="eliminator" disabled className="text-gray-800">
                Eliminator League (unavailable)
              </option>
            </select>
          </div>

          {/* Tournament Selection */}
          <div>
            <label htmlFor="tournament" className="block text-white font-medium mb-2">
              Select Tournament *
            </label>
            <select
              id="tournament"
              name="tournament"
              required
              className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="" className="text-gray-800">Choose a tournament</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id} className="text-gray-800">
                  {tournament.name} ({tournament.format})
                </option>
              ))}
            </select>
          </div>

          {/* Max Participants */}
          <div>
            <label htmlFor="max_participants" className="block text-white font-medium mb-2">
              Maximum Participants *
            </label>
            <input
              type="number"
              id="max_participants"
              name="max_participants"
              value={maxParticipants}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 2) {
                  setMaxParticipants(value);
                }
              }}
              min="2"
              max={selectedLeagueType === 'h2h' ? 2 : selectedLeagueType === 'draft_league' ? 16 : 1000}
              step="1"
              required
              className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter number of participants"
            />
            <p className="text-xs text-white/60 mt-1">
              {selectedLeagueType === 'h2h' && 'H2H leagues must have exactly 2 participants'}
              {selectedLeagueType === 'draft_league' && 'Draft leagues must have an even number of participants (max 16)'}
              {['pickem', 'budget_league', 'eliminator'].includes(selectedLeagueType) && 'Public leagues can have up to 1000 participants'}
            </p>
          </div>

          {/* League Privacy */}
          <div>
            <label className="block text-white font-medium mb-2">
              League Privacy *
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_public"
                  value="true"
                  defaultChecked
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-white">Public League - Anyone can join</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_public"
                  value="false"
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-white">Private League - Invite only (join code required)</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 bg-gradient-to-r ${leagueInfo.color} hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating League...
                </div>
              ) : (
                `Create ${leagueInfo.name}`
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold border border-white/20 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLeaguePage; 